import { useCallback, useEffect, useRef, useState } from 'react'
import { getCockpit, type CockpitPayload } from '@/lib/api'

// Module-level cache so the CaptureSheet's workstream picker (rendered on
// /concierge too) opens instantly without its own fetch. Pure server state —
// never persisted (guide gotcha #24); the server's stale-while-revalidate
// cache makes a cold fetch cheap anyway.
let lastPayload: CockpitPayload | null = null

export function getCachedCockpit(): CockpitPayload | null {
  return lastPayload
}

/** Fetch + auto-refresh the cockpit payload. Mirrors the Obsidian plugin's
 * CockpitView cadence: fetch on mount, 60s interval, refetch on focus /
 * visibilitychange when data is older than 30s (visibilitychange matters in
 * an installed PWA where `focus` doesn't reliably fire), manual `refresh(true)`
 * forces a synchronous server rebuild via ?refresh=true. */
export function useCockpitData() {
  const [payload, setPayload] = useState<CockpitPayload | null>(lastPayload)
  const [loading, setLoading] = useState(lastPayload === null)
  const [refreshing, setRefreshing] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const fetchedAt = useRef(0)
  const inFlight = useRef(false)
  const initialized = useRef(false) // strict-mode run-once guard (gotcha #26)

  const refresh = useCallback(async (force = false) => {
    if (inFlight.current) return
    inFlight.current = true
    if (force) setRefreshing(true)
    try {
      const p = await getCockpit(force)
      lastPayload = p
      fetchedAt.current = Date.now()
      setPayload(p)
      setError(null)
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e))
    } finally {
      inFlight.current = false
      setLoading(false)
      setRefreshing(false)
    }
  }, [])

  useEffect(() => {
    if (initialized.current) return
    initialized.current = true
    void refresh(false)
  }, [refresh])

  useEffect(() => {
    const interval = window.setInterval(() => void refresh(false), 60_000)
    const onWake = () => {
      if (document.visibilityState === 'hidden') return
      if (Date.now() - fetchedAt.current > 30_000) void refresh(false)
    }
    window.addEventListener('focus', onWake)
    document.addEventListener('visibilitychange', onWake)
    return () => {
      window.clearInterval(interval)
      window.removeEventListener('focus', onWake)
      document.removeEventListener('visibilitychange', onWake)
    }
  }, [refresh])

  return { payload, loading, refreshing, error, refresh }
}
